import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { IoArrowRedo } from "react-icons/io5";
import TableComponent from "../../components/table/TableComponent";
import { Column } from "../../interfaces/table.interface";
import { TuitionPeriodInterface } from "../../interfaces/tuitionPeriod.interface";
import { get } from "../../redux/ApiHelper";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import { errorToast } from "../../utils/toast.util";

const formatDate = (value?: string | Date) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { dateStyle: "medium" })
    : "N/A";

const personName = (person: unknown) => {
  if (person && typeof person === "object" && "name" in person) {
    return String((person as { name?: string }).name || "N/A");
  }
  return "N/A";
};

const resolveGcCharge = (value: TuitionPeriodInterface["gcChargePerClass"]) => {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && typeof value.coin === "number") {
    return value.coin;
  }
  return null;
};

const TuitionPeriods = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [periods, setPeriods] = useState<TuitionPeriodInterface[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0); // 0-based for TableComponent
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    dispatch(startLoading());
    const queryParams = new URLSearchParams({
      page: String(page + 1),
      limit: String(rowsPerPage),
    });
    if (debouncedSearch) {
      queryParams.set("search", debouncedSearch);
    }
    get(`/admin/tuition/tuition-periods?${queryParams.toString()}`)
      .then((res) => {
        const list = Array.isArray(res?.tuitionPeriods)
          ? res.tuitionPeriods
          : Array.isArray(res)
            ? res
            : [];
        setPeriods(list);
        setTotalResults(
          typeof res?.totalResults === "number" ? res.totalResults : list.length
        );
      })
      .catch(() => {
        errorToast("Failed to load tuition periods");
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  }, [dispatch, page, rowsPerPage, debouncedSearch]);

  const columns: Column[] = [
    {
      select: "_id",
      header: "Period ID",
      align: "left",
      render: (value: string) => (
        <span className="text-xs font-mono break-all">{value}</span>
      ),
    },
    {
      header: "Subjects",
      render: (_: unknown, row: TuitionPeriodInterface) =>
        row.subjects?.length ? row.subjects.join(", ") : "N/A",
    },
    {
      header: "Teacher",
      render: (_: unknown, row: TuitionPeriodInterface) =>
        personName(row.teacher),
    },
    {
      header: "Student",
      render: (_: unknown, row: TuitionPeriodInterface) =>
        personName(row.student),
    },
    {
      header: "Parent",
      render: (_: unknown, row: TuitionPeriodInterface) =>
        personName(row.parent),
    },
    {
      header: "Classes",
      render: (_: unknown, row: TuitionPeriodInterface) =>
        `${row.availableNumberOfClasses ?? 0}/${row.totalNumberOfClasses ?? 0}`,
    },
    {
      header: "GC / Class",
      render: (_: unknown, row: TuitionPeriodInterface) => {
        const gc = resolveGcCharge(row.gcChargePerClass);
        return gc != null ? gc : "N/A";
      },
    },
    {
      header: "Teacher Status",
      render: (_: unknown, row: TuitionPeriodInterface) => (
        <span
          className={`capitalize font-medium ${
            row.teacherStatus === "approved"
              ? "text-green-600"
              : row.teacherStatus === "rejected"
                ? "text-red-600"
                : "text-amber-600"
          }`}
        >
          {row.teacherStatus || "N/A"}
        </span>
      ),
    },
    {
      header: "Mode",
      render: (_: unknown, row: TuitionPeriodInterface) =>
        row.preferredClassMode || "N/A",
    },
    {
      header: "Created",
      render: (_: unknown, row: TuitionPeriodInterface) =>
        formatDate(row.createdAt),
    },
    {
      header: "Details",
      render: (_: unknown, row: TuitionPeriodInterface) => (
        <button
          type="button"
          onClick={() => navigate(`/tuition-periods/${row._id}`)}
          className="flex items-center gap-2 p-2 bg-slate-500 text-white text-sm font-medium rounded-full hover:bg-slate-600 transition m-auto"
          title="View details"
        >
          <IoArrowRedo className="m-auto" />
        </button>
      ),
    },
  ];

  return (
    <div className="table-container">
      <div className="title">Tuition Periods</div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search by parent/teacher name, phone, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <TableComponent
        columns={columns}
        data={periods}
        serverSidePagination={{
          page,
          rowsPerPage,
          count: totalResults,
          rowsPerPageOptions: [15, 25, 40, 100],
          onPageChange: (newPage) => setPage(newPage),
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      />
    </div>
  );
};

export default TuitionPeriods;
